import type { FilterCondition } from '@/lib/crm/components/SchemaFilterBuilder';
import type { FilterConditionGroup } from '@/lib/crm/filters/types';

/**
 * Normalizes `customize.filters` into canonical `FilterConditionGroup[]`.
 *
 * Accepts either the legacy flat `FilterCondition[]` (with an optional legacy
 * `filterLogic` of 'AND' | 'OR') or an already-group-native `FilterConditionGroup[]`.
 * This is the single normalize-on-read seam — call it at every hydration/consumption
 * point instead of branching on the shape inline.
 *
 * Empty/non-array input maps to `[]`. The empty display group shown in the wizard's
 * customize step is seeded by `CustomizeStep`, never by this function. The function
 * is idempotent when given group-native input. `filterLogic` is a legacy-only signal
 * read during migration — it has no meaning once data is stored in group-native form.
 *
 * Every condition is guaranteed a stable, non-empty, and *globally unique* `id`
 * before it reaches the builder. The builder's update/remove are both keyed on
 * `condition.id` across the whole hydrated set, so two rows sharing an id (a
 * missing id, a duplicated persisted id, or a persisted id that happens to equal
 * another row's index-derived fallback) would make editing/removing one row hit
 * the other. We normalize against the full hydrated id set — keeping the first
 * occurrence of each id and re-stamping any collision — rather than reject, so a
 * malformed row never blanks the whole report. Group ids are deduplicated the
 * same way (the builder keys group update/remove on `group.id`).
 */
export function toFilterConditionGroups(
  filters: unknown,
  filterLogic?: 'AND' | 'OR'
): FilterConditionGroup[] {
  if (!Array.isArray(filters) || filters.length === 0) {
    return [];
  }

  const usedGroupIds = new Set<string>();
  const usedConditionIds = new Set<string>();

  if (filters.every(isConditionGroupLike)) {
    return filters.map((group, i) => ({
      id: claimId(usedGroupIds, group.id, `group-${i}`),
      conditions: (Array.isArray(group.conditions) ? group.conditions : []).map(
        (condition, j) =>
          ensureConditionId(condition, usedConditionIds, `condition-${i}-${j}`)
      ),
    }));
  }

  const conditions = filters as FilterCondition[];
  if (filterLogic === 'OR') {
    return conditions.map((condition, i) => ({
      id: claimId(usedGroupIds, undefined, `group-${i}`),
      conditions: [
        ensureConditionId(condition, usedConditionIds, `condition-${i}-0`),
      ],
    }));
  }

  return [
    {
      id: claimId(usedGroupIds, undefined, 'group-0'),
      conditions: conditions.map((condition, j) =>
        ensureConditionId(condition, usedConditionIds, `condition-0-${j}`)
      ),
    },
  ];
}

/**
 * Resolves an id against the ids already claimed in this hydration pass, so no
 * two rows can end up sharing one. Keeps `candidate` when it is a non-empty
 * string not yet used; otherwise derives a unique id from `fallback` (suffixing
 * `-1`, `-2`, … only if `fallback` itself is already taken). The chosen id is
 * recorded in `used` so later rows cannot reuse it.
 */
function claimId(
  used: Set<string>,
  candidate: unknown,
  fallback: string
): string {
  let id =
    typeof candidate === 'string' && candidate.length > 0
      ? candidate
      : fallback;
  if (used.has(id)) {
    id = fallback;
    let n = 1;
    while (used.has(id)) {
      id = `${fallback}-${n}`;
      n += 1;
    }
  }
  used.add(id);
  return id;
}

/**
 * Returns the condition unchanged when its existing `id` is a non-empty string
 * that is still free in `used`; otherwise returns a copy stamped with a unique
 * id derived from `fallbackId`. The index-derived fallback
 * (`condition-<groupIndex>-<conditionIndex>`) won't collide with builder-generated
 * ids (`condition-<ts>-<n>`), and `claimId` guarantees uniqueness even when a
 * persisted condition already carries a fallback-shaped id.
 */
function ensureConditionId(
  condition: FilterCondition,
  used: Set<string>,
  fallbackId: string
): FilterCondition {
  const { id } = condition as { id?: unknown };
  const resolvedId = claimId(used, id, fallbackId);
  if (resolvedId === id) {
    return condition;
  }
  return { ...condition, id: resolvedId };
}

function isConditionGroupLike(
  value: unknown
): value is { id?: string; conditions?: unknown } {
  return (
    typeof value === 'object' &&
    value !== null &&
    'conditions' in value &&
    Array.isArray((value as { conditions?: unknown }).conditions)
  );
}
